/**
 * Gantt Chart Service
 *
 * Handles all API operations related to Gantt chart data.
 * Uses the listGanttChartDetails query following RDS direct resolver conventions.
 *
 * @module services/api/ganttChartService
 */

import { saveGanttChartSettings } from 'src/graphql/mutations';
import { listGanttChartDetails } from 'src/graphql/queries';
import {
  GanttChartFilters,
  GanttChartFiltersInput,
  GanttChartResponse,
  GanttProgramData,
  SaveGanttChartSettingsInput,
  SaveGanttChartSettingsResponse,
} from 'src/types/gantt/ganttChart.types';
import { formatApiError } from 'src/utils/errorUtils';
import { logger } from 'src/utils/logger';
import { fetchGraphQLRequest } from './clients/graphqlClient';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Transforms UI filter values to GraphQL API input format
 * Only includes mandatory fields required by the API
 *
 * @param filters - UI filter state
 * @returns GraphQL input format
 */
const transformFiltersToGraphQLInput = (filters: GanttChartFilters): GanttChartFiltersInput => {
  return {
    product_line_ids: filters.productLines,
    scenario_id: filters.scenarios,
    country: filters.countries,
    start_date: filters.streetDateRange.startDate,
    end_date: filters.streetDateRange.endDate,
  };
};

// ============================================================================
// API Operations
// ============================================================================

/**
 * Fetches Gantt chart data from the API using listGanttChartDetails query
 *
 * This uses the RDS direct resolver which is faster than Lambda-based resolvers.
 * The query follows the list* naming convention for direct database access.
 *
 * @param filters - Filter parameters for the Gantt chart query
 * @returns Promise resolving to array of Gantt program data
 * @throws Error if API call fails
 */
export const fetchGanttChartDataAPI = async (filters: GanttChartFilters): Promise<GanttProgramData[]> => {
  try {
    logger.info('Fetching Gantt chart data from API', {
      productLineCount: filters.productLines.length,
      countryCount: filters.countries.length,
      scenarioId: filters.scenarios,
      dateRange: filters.streetDateRange,
    });

    const graphqlFilters = transformFiltersToGraphQLInput(filters);

    const result = await fetchGraphQLRequest<{ listGanttChartDetails: GanttChartResponse }>({
      query: listGanttChartDetails,
      variables: {
        chart_filters: graphqlFilters,
      },
      requestName: 'listGanttChartDetails',
      requestType: 'query',
    });

    const response = result.listGanttChartDetails;

    // Check for errors in the response
    if (response.error) {
      logger.error('Gantt chart API returned error:', { error: response.error });
      throw new Error(response.error);
    }

    const data = response.body || [];

    logger.info('Successfully fetched Gantt chart data from API', {
      productLineCount: data.length,
      totalPrograms: data.reduce((sum, pl) => sum + (pl.rpns?.length || 0), 0),
    });

    return data;
  } catch (error) {
    logger.error('Error fetching Gantt chart data from API:', { error });
    throw formatApiError('Failed to fetch Gantt chart data', error);
  }
};

/**
 * Saves Gantt chart settings (hardware development status, MSRP selection)
 *
 * @param settings - Array of settings to save
 * @returns Promise resolving to save response
 * @throws Error if API call fails
 */
export const saveGanttChartSettingsAPI = async (
  settings: SaveGanttChartSettingsInput[]
): Promise<SaveGanttChartSettingsResponse> => {
  try {
    logger.info('Saving Gantt chart settings', {
      recordCount: settings.length,
      rpnIds: settings.map((s) => s.verity_rpn_id),
    });

    const result = await fetchGraphQLRequest<{ saveGanttChartSettings: { numberOfRecordsUpdated: number } }>({
      query: saveGanttChartSettings,
      variables: {
        input: settings.map((setting) => ({
          ...setting,
          // Ensure hardware_development_status has a valid default
          hardware_development_status: setting.hardware_development_status || 'NOT_SET',
        })),
      },
      requestName: 'saveGanttChartSettings',
      requestType: 'mutation',
      auditConfig: { pageName: 'gantt_chart_settings' },
    });

    const response = result.saveGanttChartSettings;

    logger.info('Successfully saved Gantt chart settings', {
      numberOfRecordsUpdated: response.numberOfRecordsUpdated,
    });

    return {
      success: true,
      message: `Successfully updated ${response.numberOfRecordsUpdated} record(s)`,
      numberOfRecordsUpdated: response.numberOfRecordsUpdated,
    };
  } catch (error) {
    logger.error('Error saving Gantt chart settings:', { error });
    throw formatApiError('Failed to save Gantt chart settings', error);
  }
};
